import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import type { Message } from '@orbit-sync/shared-types';
import { useAuthStore } from '../auth/authStore.js';
import { useChatStore } from '../chat/chatStore.js';
import { useDirectoryStore } from '../chat/directoryStore.js';
import { memberLabel } from '../chat/labels.js';
import { notificationBody, shouldNotify } from './policy.js';
import { useNotificationStore } from './notificationStore.js';
import { lastPushFor, listenToServiceWorker } from '../pwa/serviceWorker.js';
import { setNotificationNavigator, show } from './systemNotifications.js';

/**
 * With Web Push active the server notifies this device too, and the service
 * worker shows that. The page waits this long for the push before raising its
 * own - so a server that cannot reach the push services (no internet on the
 * VM) still leaves the open app notifying, without a double alert otherwise.
 */
const PUSH_GRACE_MS = 4_000;
/** A push this much older than the socket message still counts (it can arrive first). */
const PUSH_LEAD_MS = 15_000;

/**
 * Turns an arriving message into a system notification, when it warrants one.
 *
 * Returns a callback rather than subscribing to anything itself: the socket is
 * already bridged to the store in `chat/useChatSocket.ts`, and a second listener
 * for the same event would be a second place to keep in step. The bridge calls
 * this with each message it receives.
 *
 * It reads the chat and directory stores with `getState()` instead of hooks, so
 * an arriving message does not re-render whatever mounted it - the callback is
 * stable for the lifetime of the component.
 */
export function useMessageNotifications(): (message: Message) => void {
  const navigate = useNavigate();
  const refresh = useNotificationStore((state) => state.refresh);
  const syncPush = useNotificationStore((state) => state.syncPush);

  // Notification clicks route inside the app; and, signed in, this browser
  // (re)registers for Web Push - for this account and session.
  useEffect(() => {
    setNotificationNavigator(navigate);
    const stop = listenToServiceWorker(navigate);
    void syncPush();

    return () => {
      stop();
      setNotificationNavigator(null);
    };
  }, [navigate, syncPush]);

  // A grant can be revoked from the address bar while the tab is open, and
  // nothing tells us. Re-reading it when the tab comes back to the foreground
  // keeps the account menu honest without polling.
  useEffect(() => {
    function onVisibility(): void {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    }

    document.addEventListener('visibilitychange', onVisibility);

    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [refresh]);

  return useCallback((message: Message) => {
    const { enabled, permission } = useNotificationStore.getState();
    const currentUserId = useAuthStore.getState().user?.id ?? null;
    const chat = useChatStore.getState();

    const allowed = shouldNotify({
      message,
      currentUserId,
      activeChannelId: chat.activeChannelId,
      documentVisible: document.visibilityState === 'visible',
      windowFocused: document.hasFocus(),
      enabled,
      permission,
    });

    if (!allowed) {
      return;
    }

    const channel = chat.channels.find((item) => item.id === message.channelId);
    const directory = useDirectoryStore.getState().names;
    const sender = memberLabel(message.senderId, currentUserId, directory);

    // The sender may be someone this session has never rendered, so their name
    // is not in the cache yet. Ask for it: this notification falls back to the
    // id fragment, and the conversation it opens will have the real name.
    useDirectoryStore.getState().ensure([message.senderId]);

    const isDirect = channel?.type === 'direct';
    const inGroup = channel && !isDirect;

    // A direct message is titled by the person; a group message by the channel,
    // with the person on the body line - which is where the eye goes second.
    const title = inGroup ? channel.name : sender;
    const body = inGroup
      ? `${sender}: ${notificationBody(message.content)}`
      : notificationBody(message.content);

    const notification = {
      title,
      body,
      // One entry per conversation: a burst of messages replaces itself rather
      // than filling the system tray. The service worker's pushes use the same tag.
      tag: `orbit-sync:${message.channelId}`,
      url: `/channels/${message.channelId}`,
    };

    if (!useNotificationStore.getState().pushActive) {
      void show(notification);
      return;
    }

    const arrivedAt = Date.now();
    window.setTimeout(() => {
      if (lastPushFor(message.channelId) < arrivedAt - PUSH_LEAD_MS) {
        void show(notification);
      }
    }, PUSH_GRACE_MS);
  }, []);
}
